import type { Sample } from 'src/shared/types';

export type SampleWithLocation = {
    location?: { coordinates?: Sample['location']['coordinates'] | null } | null;
};

export type Coordinate = { latitude: number; longitude: number };

/** One point to represent a project whose habitat sites have no polygons: its
 *  first sample carrying a location, or null if none does.
 *
 *  Takes an actual sample rather than averaging them, so the result is always
 *  somewhere fieldwork happened — an average can land in the bay between two
 *  coastal samples, or in the middle of a lake.
 *
 *  Samples arrive as `[latitude, longitude]`, the opposite order to the GeoJSON
 *  the engine stores and to the `boundingBox` field on the same GraphQL query.
 *  This is the only place that order is read. See DD-6045 for why the API is
 *  inconsistent. `samples` is nullable because the Go API answers null for a
 *  project that has none. */
export const pickRepresentativePoint = (samples: SampleWithLocation[] | null | undefined): Coordinate | null => {
    for (const { location } of samples ?? []) {
        const [latitude, longitude] = location?.coordinates ?? [];
        const hasValidLocation = latitude !== undefined && longitude !== undefined && !(latitude === 0 && longitude === 0);
        // Only the 0,0 pair is unusable, and a truthiness check would also drop
        // a real 0 on one axis — the prime meridian and the equator. Skipping
        // the pair does not distinguish an absent location from a sample truly
        // at 0,0, because the API sends the same value for both (DD-6049), so
        // it rests on exact zeroes being an artifact rather than a GPS reading.
        if (hasValidLocation) {
            return { latitude, longitude };
        }
    }
    return null;
};
